import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../utils/api';
import './Events.css';

const FALLBACK_IMG = '/assets/hero/hero-bg.webp';

function fmtDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  const sameDay = s.toDateString() === e.toDateString();
  if (sameDay) {
    const date = s.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const startTime = s.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
    // No distinct end → show a single time instead of "7:30 am – 7:30 am".
    if (s.getTime() === e.getTime()) {
      return `${date} · ${startTime}`;
    }
    const endTime = e.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${startTime} – ${endTime}`;
  }
  return `${fmtDateTime(start)} – ${fmtDateTime(end)}`;
}

function RegistrationForm({ event, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.registerForEvent({ event_id: event.id, ...form });
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Could not register. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit}>
      {error && <div className="event-form__error">{error}</div>}
      <div className="field">
        <label htmlFor="reg-name">Your name</label>
        <input id="reg-name" name="name" value={form.name} onChange={handleChange} required maxLength={120} />
      </div>
      <div className="field">
        <label htmlFor="reg-email">Email</label>
        <input id="reg-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={190} />
      </div>
      <div className="field">
        <label htmlFor="reg-phone">Phone (optional)</label>
        <input id="reg-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} maxLength={40} />
      </div>
      <div className="field">
        <label htmlFor="reg-message">Anything we should know? (optional)</label>
        <textarea id="reg-message" name="message" value={form.message} onChange={handleChange} maxLength={4000} />
      </div>
      <button type="submit" className="btn-primary event-sidebar__cta" disabled={submitting}>
        {submitting ? 'Registering…' : 'Register now'}
      </button>
    </form>
  );
}

function EnquiryForm({ event, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.submitEventEnquiry({ event_id: event.id, ...form });
      onSuccess(result);
    } catch (err) {
      setError(err.message || 'Could not send your enquiry. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit}>
      {error && <div className="event-form__error">{error}</div>}
      <div className="field">
        <label htmlFor="enq-name">Your name</label>
        <input id="enq-name" name="name" value={form.name} onChange={handleChange} required maxLength={120} />
      </div>
      <div className="field">
        <label htmlFor="enq-email">Email</label>
        <input id="enq-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={190} />
      </div>
      <div className="field">
        <label htmlFor="enq-phone">Phone (optional)</label>
        <input id="enq-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} maxLength={40} />
      </div>
      <div className="field">
        <label htmlFor="enq-message">What would you like to know? (optional)</label>
        <textarea id="enq-message" name="message" value={form.message} onChange={handleChange} maxLength={4000}
                  placeholder="Pricing, dates, group bookings, anything else…" />
      </div>
      <button type="submit" className="btn-primary event-sidebar__cta" disabled={submitting}>
        {submitting ? 'Sending…' : 'Send enquiry'}
      </button>
    </form>
  );
}

function PaymentScreenshotUpload({ registrationId, uploadToken }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please choose an image first.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image is larger than 5 MB.'); return; }
    setError('');
    setUploading(true);
    try {
      const result = await api.uploadEventPaymentProof({
        registration_id: registrationId,
        upload_token: uploadToken,
        file,
      });
      setUploadedUrl(result.screenshot);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (uploadedUrl) {
    return (
      <div style={{
        marginTop: 16, padding: 14, background: '#ECFDF5',
        border: '1px solid #A7F3D0', borderRadius: 8,
      }}>
        <p style={{ margin: 0, color: '#065F46', fontSize: 14, fontWeight: 600 }}>
          ✓ Screenshot received — we'll confirm your payment shortly.
        </p>
        <img
          src={uploadedUrl}
          alt="Your payment screenshot"
          style={{ display: 'block', maxWidth: 220, marginTop: 10, borderRadius: 6, border: '1px solid #D1FAE5' }}
        />
        <button
          type="button"
          onClick={() => { setUploadedUrl(null); setFile(null); }}
          style={{
            marginTop: 10, background: 'transparent', border: 0,
            color: '#065F46', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Upload a different image
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16, padding: 14, background: 'var(--bg-section)', borderRadius: 8 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 700,
        color: 'var(--text-heading)', textTransform: 'uppercase',
        letterSpacing: 0.6, marginBottom: 6,
      }}>
        Upload payment screenshot (optional)
      </label>
      <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-light)' }}>
        Once you've paid, drop a screenshot here so we can verify and confirm your seat faster.
        JPG, PNG or WEBP · max 5 MB.
      </p>
      {error && (
        <div style={{
          background: '#FEF2F2', color: '#B91C1C',
          padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 10,
        }}>{error}</div>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => setFile(e.target.files && e.target.files[0])}
        disabled={uploading}
        style={{ display: 'block', marginBottom: 10, fontSize: 13 }}
      />
      <button
        type="submit"
        className="btn-primary"
        disabled={uploading || !file}
        style={{ padding: '10px 18px', fontSize: 13.5, opacity: uploading || !file ? 0.6 : 1 }}
      >
        {uploading ? 'Uploading…' : 'Submit screenshot'}
      </button>
    </form>
  );
}

function PaymentSuccess({ result }) {
  const { event } = result;
  const { payment_bank_details, payment_instructions, payment_qr_image } = event || {};
  const hasFree = !event || Number(event.cost_inr) === 0;

  return (
    <div className="event-success">
      <h3 className="event-success__title">You're registered.</h3>
      <p className="event-success__msg">{result.message}</p>
      {!hasFree && (
        <div className="event-success__pay">
          <h4>How to pay</h4>
          {payment_instructions && (
            <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>
              {payment_instructions}
            </p>
          )}
          {payment_bank_details && <pre>{payment_bank_details}</pre>}
          {payment_qr_image && (
            <img src={payment_qr_image} alt="Payment QR" className="event-success__qr" />
          )}
          <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--text-light)' }}>
            After paying, please share the transaction reference on WhatsApp at{' '}
            <a href="https://wa.me/919140395816" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
              +91 9140395816
            </a>{' '}so we can confirm your seat.
          </p>
          {result.registration_id && result.upload_token && (
            <PaymentScreenshotUpload
              registrationId={result.registration_id}
              uploadToken={result.upload_token}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RegistrationModal({ event, isEnquire, existingRegistration, onClose, onRegister }) {
  // Start at step 2 if we're reopening after a paid registration — the user
  // already filled the form and just wants to revisit the payment details.
  const initialStep = existingRegistration && !isEnquire ? 2 : 1;
  const [step, setStep] = useState(initialStep);

  const handleSuccess = (result) => {
    onRegister(result);
    if (!isEnquire) setStep(2);
  };

  // Esc-to-close + lock background scroll while the modal is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="event-modal" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
      <div className="event-modal__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="event-modal__close" onClick={onClose} aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="event-modal__header">
          <p className="event-modal__eyebrow">{isEnquire ? 'Enquire about' : 'Register for'}</p>
          <h2 id="event-modal-title" className="event-modal__title">{event.title}</h2>
        </div>

        {!isEnquire && (
          <ol className="event-modal__steps" aria-label="Registration progress">
            <li className={`event-modal__step ${step >= 1 ? 'is-active' : ''} ${step > 1 ? 'is-done' : ''}`}>
              <span className="event-modal__step-num">1</span> Your details
            </li>
            <li className="event-modal__step-sep" aria-hidden="true" />
            <li className={`event-modal__step ${step >= 2 ? 'is-active' : ''}`}>
              <span className="event-modal__step-num">2</span> Payment
            </li>
          </ol>
        )}

        <div className="event-modal__body">
          {isEnquire ? (
            existingRegistration ? (
              <div className="event-success">
                <h3 className="event-success__title">Enquiry received.</h3>
                <p className="event-success__msg">{existingRegistration.message}</p>
                <button type="button" className="btn-primary event-sidebar__cta" onClick={onClose}>Done</button>
              </div>
            ) : (
              <EnquiryForm event={event} onSuccess={handleSuccess} />
            )
          ) : step === 1 ? (
            <RegistrationForm event={event} onSuccess={handleSuccess} />
          ) : (
            <>
              <PaymentSuccess result={existingRegistration} />
              <button type="button" className="btn-outline event-sidebar__cta" style={{ marginTop: 16 }} onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.getEventBySlug(slug)
      .then((data) => {
        if (!active) return;
        setEvent(data);
        document.title = `${data.title} · Mind Spa`;
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (loading) {
    return <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>Loading event…</div>;
  }
  if (notFound || !event) {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <h2>Event not found</h2>
        <p style={{ marginTop: 12 }}>
          <Link to="/events" style={{ color: 'var(--accent)' }}>← Back to all events</Link>
        </p>
      </div>
    );
  }

  const isEnquire = event.pricing_mode === 'enquire';
  const price = Number(event.cost_inr) || 0;
  const seatsLeft = event.capacity != null ? Math.max(0, event.capacity - (event.seats_taken || 0)) : null;
  const regOpen = event.registration_open && (seatsLeft === null || seatsLeft > 0);
  const eventType = event.event_type || 'offline';
  const eventTypeLabel = eventType === 'online' ? 'Online' : eventType === 'hybrid' ? 'Hybrid' : 'In-person';
  const priceLabel = isEnquire ? 'Enquire for pricing' : (price > 0 ? `₹${price.toLocaleString('en-IN')}` : 'Free');

  return (
    <div className="event-detail">
      {/* Hero */}
      <section className="event-hero">
        <div className="event-hero__bg">
          <img src={event.image || FALLBACK_IMG} alt="" />
        </div>
        <div className="event-hero__overlay" />
        <div className="container event-hero__inner">
          <div className="event-hero__crumbs">
            <Link to="/">Home</Link> &nbsp;/&nbsp; <Link to="/events">Events</Link> &nbsp;/&nbsp; <span>{event.title}</span>
          </div>
          <h1 className="event-hero__title">{event.title}</h1>
          {event.subtitle && <p className="event-hero__subtitle">{event.subtitle}</p>}
          <div className="event-hero__meta">
            <span>📅 {fmtDateRange(event.start_date, event.end_date)}</span>
            {event.venue && <span>📍 {event.venue}</span>}
            <span>{eventType === 'online' ? '💻' : eventType === 'hybrid' ? '🔀' : '🏛️'} {eventTypeLabel}</span>
            <span>💳 {priceLabel}</span>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="event-detail__grid">
          {/* Main column */}
          <div>
            <div className="event-card-panel">
              <h2 className="event-card-panel__title">About this event</h2>
              <div className="event-card-panel__body" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>

            {event.timetable && event.timetable.trim() !== '' && (
              <div className="event-card-panel">
                <h2 className="event-card-panel__title">Timetable</h2>
                <div className="event-card-panel__body" dangerouslySetInnerHTML={{ __html: event.timetable }} />
              </div>
            )}

            {event.gallery && event.gallery.length > 0 && (
              <div className="event-card-panel">
                <h2 className="event-card-panel__title">Gallery</h2>
                <div className="event-gallery-grid">
                  {event.gallery.map((item) => {
                    const url = item.url || item.image;
                    const isVideo = item.type === 'video';
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="event-gallery-tile"
                        onClick={() => setLightbox(item)}
                        aria-label={isVideo ? 'Play video' : 'Enlarge image'}
                        style={{ border: 0, padding: 0, position: 'relative' }}
                      >
                        {isVideo ? (
                          <>
                            <video
                              src={url}
                              muted
                              playsInline
                              preload="metadata"
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <span
                              aria-hidden="true"
                              style={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(0,0,0,0.25)',
                              }}
                            >
                              <svg width="42" height="42" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0.5">
                                <polygon points="6 4 20 12 6 20 6 4" />
                              </svg>
                            </span>
                          </>
                        ) : (
                          <img src={url} alt={item.caption || ''} loading="lazy" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="event-sidebar">
            <div className="event-card-panel">
              <div className="event-sidebar__price">
                {isEnquire ? 'Enquire' : (price > 0 ? `₹${price.toLocaleString('en-IN')}` : 'Free')}
              </div>
              <p className="event-sidebar__price-note">
                {isEnquire ? 'Pricing on request' : (price > 0 ? 'per attendee' : 'No payment required')}
              </p>

              <dl style={{ margin: 0 }}>
                <div className="event-sidebar__row">
                  <dt>Starts</dt><dd>{fmtDateTime(event.start_date)}</dd>
                </div>
                {event.end_date && event.end_date !== event.start_date && (
                  <div className="event-sidebar__row">
                    <dt>Ends</dt><dd>{fmtDateTime(event.end_date)}</dd>
                  </div>
                )}
                <div className="event-sidebar__row">
                  <dt>Format</dt><dd>{eventTypeLabel}</dd>
                </div>
                {event.venue && (
                  <div className="event-sidebar__row">
                    <dt>Venue</dt><dd>{event.venue}</dd>
                  </div>
                )}
                {!isEnquire && seatsLeft !== null && (
                  <div className="event-sidebar__row">
                    <dt>Seats left</dt><dd>{seatsLeft} of {event.capacity}</dd>
                  </div>
                )}
              </dl>

              {(isEnquire || regOpen) ? (
                registration ? (
                  <div style={{ marginTop: 18 }}>
                    <div className="event-sidebar__registered">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {isEnquire ? 'Enquiry sent — we\'ll be in touch.' : "You're registered."}
                    </div>
                    {!isEnquire && (
                      <button
                        type="button"
                        className="btn-outline event-sidebar__cta"
                        onClick={() => setModalOpen(true)}
                      >
                        View payment details
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-primary event-sidebar__cta"
                    onClick={() => setModalOpen(true)}
                    style={{ marginTop: 18 }}
                  >
                    {isEnquire ? 'Enquire now' : 'Register now'}
                  </button>
                )
              ) : (
                <div className="event-sidebar__closed">
                  {seatsLeft === 0
                    ? 'This event is full.'
                    : 'Registration is closed for this event.'}
                  <div style={{ marginTop: 10 }}>
                    <Link to="/contact" style={{ color: 'var(--accent)' }}>Contact us to be added to the waitlist →</Link>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {modalOpen && (
        <RegistrationModal
          event={event}
          isEnquire={isEnquire}
          existingRegistration={registration}
          onClose={() => setModalOpen(false)}
          onRegister={setRegistration}
        />
      )}

      {lightbox && (
        <div className="event-lightbox" onClick={() => setLightbox(null)} role="dialog">
          <button type="button" className="event-lightbox__close" onClick={() => setLightbox(null)} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          {lightbox.type === 'video' ? (
            <video
              src={lightbox.url || lightbox.image}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 6, background: '#000' }}
            />
          ) : (
            <img src={lightbox.url || lightbox.image} alt="" onClick={(e) => e.stopPropagation()} />
          )}
        </div>
      )}
    </div>
  );
}

export default EventDetail;
